// Figma design: https://www.figma.com/design/sseCNpJYq9VS8RO8p4u6HJ/Ticket-Purchase-Flow?node-id=17209-48797

import { yupResolver } from '@hookform/resolvers/yup';
import { Alert, AlertTitle, Box, Button, Stack, TextField, Typography } from '@mui/material';
import { SwipableDialog } from '@tix-ticketing/ui-components';
import { CheckIcon } from '@tix-ticketing/ui-icons/outline';
import type { ChangeEvent } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import type { DiscountClubDto, DiscountClubPreviewDto, OrderResponse } from 'api/generated/types.gen';
import { buildDiscountClubSchema, emptyDiscountClubValues, svgDataUri, toDiscountClubFields, type DiscountClubFormValues } from 'lib/helpers/discount-club-form';
import { FormattedPrice } from 'lib/helpers/formatted-price';
import { applyMask, formatPatternMask } from 'lib/helpers/input-mask';
import { useApplyDiscountClubCard, usePreviewDiscountClubCard } from 'lib/hooks/use-discount-clubs';

/** ProblemDetails with the club's own reason for declining the card. */
type ProblemResponse = {
  detail?: string | null;
  reason?: string | null;
};
interface Problem {
  title: string;
  reason: string | null;
}
interface DiscountClubDialogProps {
  open: boolean;
  onClose: () => void;
  club: DiscountClubDto;
  order: OrderResponse;
  onApplied: () => void;
}
const inputModes: Record<string, 'numeric' | 'text' | 'email' | 'tel'> = {
  number: 'numeric',
  numeric: 'numeric',
  email: 'email',
  tel: 'tel'
};

/**
 * Card entry for a discount club. The fields are the club's own, as the
 * backend defines them. Continue prices the card against the order without
 * saving anything; the buyer then applies it.
 */
export const DiscountClubDialog = ({
  open,
  onClose,
  club,
  order,
  onApplied
}: DiscountClubDialogProps) => {
  const intl = useIntl();
  const fields = useMemo(() => (club.form?.fields ?? []).filter(field => field.key), [club.form?.fields]);
  const [preview, setPreview] = useState<DiscountClubPreviewDto | null>(null);
  const [problem, setProblem] = useState<Problem | null>(null);
  const schema = useMemo(() => buildDiscountClubSchema(fields, intl), [fields, intl]);
  const {
    register,
    handleSubmit,
    setFocus,
    reset,
    formState: {
      errors
    }
  } = useForm<DiscountClubFormValues>({
    resolver: yupResolver(schema),
    defaultValues: emptyDiscountClubValues(fields)
  });
  const firstFieldKey = fields[0]?.key;
  useEffect(() => {
    if (!open) return;
    setPreview(null);
    setProblem(null);
    reset(emptyDiscountClubValues(fields));
    const timer = setTimeout(() => {
      if (firstFieldKey) setFocus(firstFieldKey);
    }, 100);
    return () => clearTimeout(timer);
  }, [open, fields, firstFieldKey, reset, setFocus]);
  const previewMutation = usePreviewDiscountClubCard();
  const applyMutation = useApplyDiscountClubCard();

  // The detail says what went wrong and the reason why; the title is only
  // the HTTP status text.
  const showProblem = (data?: ProblemResponse | null) => {
    setPreview(null);
    setProblem({
      title: data?.detail || intl.formatMessage({
        id: "cart.discountClub.invalid",
        defaultMessage: [{
          "type": 0,
          "value": "Card number invalid. Check your input and try again."
        }]
      }),
      reason: data?.reason ?? null
    });
    if (firstFieldKey) setFocus(firstFieldKey);
  };
  const variables = (values: DiscountClubFormValues) => ({
    encryptedOrderId: order.encryptedId ?? '',
    couponTypeId: club.couponTypeId ?? 0,
    fields: toDiscountClubFields(fields, values)
  });
  const onPreview: SubmitHandler<DiscountClubFormValues> = values => {
    if (!order.encryptedId) return;
    setProblem(null);
    previewMutation.mutate(variables(values), {
      onSuccess: (data: any) => {
        if (data.status !== 200) return showProblem(data.response?.data);
        setPreview(data.data ?? {
          tickets: []
        });
      },
      onError: (error: any) => showProblem(error?.response?.data)
    });
  };
  const onApply: SubmitHandler<DiscountClubFormValues> = values => {
    if (!order.encryptedId) return;
    applyMutation.mutate(variables(values), {
      onSuccess: (data: any) => {
        if (data.status !== 200) return showProblem(data.response?.data);
        onApplied();
        onClose();
      },
      onError: (error: any) => showProblem(error?.response?.data)
    });
  };

  // The preview's tickets per event, in the order's event order.
  const previewEvents = useMemo(() => {
    const tickets = preview?.tickets ?? [];
    return (order.events ?? []).map(event => {
      const eventTickets = tickets.filter(t => t.eventId === event.id);
      return {
        event,
        count: eventTickets.length,
        total: eventTickets.reduce((sum, t) => sum + (t.price ?? 0), 0)
      };
    }).filter(({
      count
    }) => count > 0);
  }, [preview, order.events]);
  const canApply = previewEvents.length > 0;
  const isPending = previewMutation.isPending || applyMutation.isPending;
  const logo = svgDataUri(club.form?.logo?.color);
  const clubName = club.form?.title || club.name || '';
  const clearResult = () => {
    if (preview || problem) {
      setPreview(null);
      setProblem(null);
    }
  };
  return <SwipableDialog open={open} onClose={onClose} maxWidth="sm"
  // Without a logo the dialog's own header names the club.
  headerText={logo ? undefined : clubName} subtitleText={logo ? undefined : club.form?.subtitle ?? undefined} primaryActionButton={<Button data-testid="discount-club-submit" type="submit" size="large" variant="contained" fullWidth color={canApply ? 'success' : 'primary'} startIcon={canApply ? <CheckIcon /> : undefined} loading={isPending} onClick={handleSubmit(canApply ? onApply : onPreview)}>
          {canApply ? <FormattedMessage id="cart.discountClub.applyDiscount" defaultMessage={[{
      "type": 0,
      "value": "Apply discount"
    }]} /> : <FormattedMessage id="common.continue" defaultMessage={[{
      "type": 0,
      "value": "Continue"
    }]} />}
        </Button>}>
      <Stack spacing={3}>
        {logo && <Stack spacing={2} sx={{
        alignItems: 'center'
      }}>
            <Box component="img" src={logo} alt={clubName} sx={{
          height: theme => theme.spacing(6),
          maxWidth: '100%'
        }} />
            {club.form?.subtitle && <Typography variant="body1" sx={{
          color: 'text.secondary',
          textAlign: 'center'
        }}>
                {club.form.subtitle}
              </Typography>}
          </Stack>}
        <Stack component="form" spacing={2} onSubmit={handleSubmit(canApply ? onApply : onPreview)}>
          {fields.map(field => {
          const key = field.key ?? '';
          const mask = field.mask;
          const registered = register(key);
          const onChange = async (event: ChangeEvent<HTMLInputElement>) => {
            if (mask) {
              applyMask(event.target, value => formatPatternMask(value, mask));
            }
            await registered.onChange(event);
            clearResult();
          };
          return <TextField key={key} fullWidth label={field.label} placeholder={field.placeholder ?? undefined} required={field.required} color={problem ? 'warning' : 'primary'} error={!!errors[key]} helperText={errors[key]?.message ?? ''} {...registered} onChange={onChange} slotProps={{
            htmlInput: {
              'data-testid': `discount-club-field-${key}`,
              inputMode: inputModes[field.type ?? ''],
              autoComplete: 'off',
              spellCheck: false,
              // Edits during a request would leave its response
              // describing values that are no longer in the form.
              readOnly: isPending,
              ...(mask && {
                autoCapitalize: 'characters'
              })
            }
          }} />;
        })}
          {/* Submitting with Enter needs a submit control inside the form. */}
          <button type="submit" hidden />
        </Stack>
        {problem && <Alert severity="warning" data-testid="discount-club-problem">
            <AlertTitle>{problem.title}</AlertTitle>
            {problem.reason}
          </Alert>}
        {preview && !canApply && <Alert severity="warning">
            <FormattedMessage id="cart.discountClub.noTicketsDiscounted" defaultMessage={[{
          "type": 0,
          "value": "This card doesn't discount any tickets in your cart."
        }]} />
          </Alert>}
        {canApply && previewEvents.map(({
        event,
        count,
        total
      }) => <Alert key={event.id} severity="success" icon={false} data-testid="discount-club-preview" sx={{
        '& .MuiAlert-message': {
          width: '100%'
        }
      }}>
              <Typography variant="body1Medium" component="p">
                {event.name}
              </Typography>
              {event.subTitle && <Typography variant="body2" component="p">
                  {event.subTitle}
                </Typography>}
              <Stack direction="row" spacing={1} sx={{
          justifyContent: 'space-between',
          mt: 1
        }}>
                <Typography variant="body2Medium">
                  <FormattedMessage id="cart.discountClub.discountedTicketPrice" defaultMessage={[{
              "type": 6,
              "value": "count",
              "options": {
                "one": {
                  "value": [{
                    "type": 7
                  }, {
                    "type": 0,
                    "value": " × discounted ticket price"
                  }]
                },
                "other": {
                  "value": [{
                    "type": 7
                  }, {
                    "type": 0,
                    "value": " × discounted ticket prices"
                  }]
                }
              },
              "offset": 0,
              "pluralType": "cardinal"
            }]} values={{
              count
            }} />
                </Typography>
                <Typography variant="body2Medium">
                  <FormattedPrice amount={total} />
                </Typography>
              </Stack>
            </Alert>)}
      </Stack>
    </SwipableDialog>;
};